/**
 * Smart product-video player. Accepts either an uploaded file URL
 * (/api/files/...) or a link to YouTube / Facebook / TikTok and renders
 * the appropriate native or embed player.
 */
import { extractYouTubeId } from "./YouTubeHero";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function isUploaded(url) {
  return /^\/api\/files\//.test(url) || /\.(mp4|webm|mov|m4v)(\?|$)/i.test(url);
}

function detectKind(url) {
  if (!url) return null;
  if (/youtube\.com|youtu\.be/i.test(url)) return "youtube";
  if (/facebook\.com|fb\.watch/i.test(url)) return "facebook";
  if (/tiktok\.com/i.test(url)) return "tiktok";
  if (isUploaded(url)) return "file";
  return "link";
}

function extractTikTokId(url) {
  // matches /video/<digits> in tiktok.com URLs (regular or short)
  const m = String(url).match(/\/video\/(\d+)/);
  return m ? m[1] : null;
}

export function detectVideoKind(url) {
  return detectKind(url);
}

export default function ProductVideo({ url }) {
  if (!url) return null;
  const kind = detectKind(url);

  let body = null;
  if (kind === "file") {
    const src = url.startsWith("http") ? url : `${BACKEND}${url}`;
    body = (
      <video
        src={src}
        controls
        preload="metadata"
        playsInline
        className="absolute inset-0 w-full h-full bg-black"
        data-testid="product-video-file"
      />
    );
  } else if (kind === "youtube") {
    const id = extractYouTubeId(url);
    if (!id) return null;
    body = (
      <iframe
        src={`https://www.youtube.com/embed/${id}?rel=0&modestbranding=1&playsinline=1`}
        title="Product video"
        className="absolute inset-0 w-full h-full"
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        data-testid="product-video-youtube"
      />
    );
  } else if (kind === "facebook") {
    const src = `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}&show_text=0`;
    body = (
      <iframe
        src={src}
        title="Product video"
        className="absolute inset-0 w-full h-full"
        frameBorder="0"
        scrolling="no"
        allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
        allowFullScreen
        data-testid="product-video-facebook"
      />
    );
  } else if (kind === "tiktok") {
    const id = extractTikTokId(url);
    if (id) {
      body = (
        <iframe
          src={`https://www.tiktok.com/embed/v2/${id}`}
          title="Product video"
          className="absolute inset-0 w-full h-full"
          frameBorder="0"
          allow="encrypted-media; picture-in-picture"
          allowFullScreen
          data-testid="product-video-tiktok"
        />
      );
    } else {
      body = (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="absolute inset-0 grid place-items-center bg-black text-white font-display font-bold"
          data-testid="product-video-tiktok-link"
        >
          ▶ Watch on TikTok
        </a>
      );
    }
  } else {
    body = (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="absolute inset-0 grid place-items-center bg-black text-white font-display font-bold"
        data-testid="product-video-link"
      >
        ▶ Watch video
      </a>
    );
  }

  const labelMap = {
    file: "Seller video",
    youtube: "YouTube",
    facebook: "Facebook",
    tiktok: "TikTok",
    link: "Video",
  };

  return (
    <div className="mt-6" data-testid="product-video-wrapper">
      <div className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 font-bold mb-2">
        ▶ {labelMap[kind]}
      </div>
      <div className="relative aspect-video w-full border border-neutral-200 bg-black overflow-hidden">
        {body}
      </div>
    </div>
  );
}
